<!-- components/CookiePreferencesModal.vue -->
<script setup lang="ts">
import { useCookieConsentStore, type CookiePreferences } from '~/stores/cookieConsent'
import { computed, onMounted, ref } from 'vue'
import { useModal } from '#imports'

const preferencesModalController = useModal()
const cookieStore = useCookieConsentStore()
const preferences = ref<CookiePreferences>({ ...cookieStore.preferences })
const preferencesValue = computed(() => preferences.value)

onMounted(() => {
  preferences.value = { ...cookieStore.preferences }
})

const updatePreference = (service: keyof CookiePreferences, value: boolean) => {
  if (service === 'necessary') return
  preferences.value[service] = value
  // cookieStore.updatePreferences({ [service]: value })
}

const handleCheckboxChange = (event: Event, service: keyof CookiePreferences) => {
  const target = event.target as HTMLInputElement
  if (target) {
    updatePreference(service, target.checked)
  }
}

const handleAcceptAll = () => {
  cookieStore.acceptAll()
  preferencesModalController.close()
}

const handleAcceptSelected = () => {
  cookieStore.updatePreferences({ ...preferences.value })
  cookieStore.acceptSelected()
  preferencesModalController.close()
}

defineExpose({ open: preferencesModalController.open })

const serviceList: { key: keyof CookiePreferences }[] = [
  { key: 'googleAnalytics' },
  { key: 'googleMaps' },
  { key: 'youtube' },
  { key: 'microsoftBing' },
  { key: 'googleTagManager' },
]
</script>

<template>
  <SharedModal :controller="preferencesModalController">
    <div class="min-h-full flex flex-col p-0 md:p-6">
      <h3 class="text-left w-full text-lg md:text-xl font-medium text-primary mb-4">
        {{ $t('cookieConsent.individualSettingsTitle') }}
      </h3>
      <div class="space-y-4">
        <div class="flex flex-col p-4 bg-gray-50 rounded-lg mb-2">
          <div class="flex items-center justify-between">
            <div>
              <div class="font-bold">{{ $t('cookieConsent.services.necessary.title') }}</div>
              <div class="text-xs font-bold text-gray-400">{{ $t('cookieConsent.alwaysActive') }}</div>
            </div>
            <input type="checkbox" :checked="preferencesValue.necessary" disabled class="custom-checkbox" />
          </div>
          <div class="text-sm text-gray-600 mt-2">
            {{ $t('cookieConsent.services.necessary.description') }}
          </div>
        </div>

        <div class="flex flex-col p-4 bg-gray-50 rounded-lg mb-2">
          <div class="flex items-center justify-between">
            <div>
              <div class="font-bold">{{ $t('cookieConsent.services.matomo.title') }}</div>
              <div class="text-xs font-bold text-gray-400">{{ $t('cookieConsent.alwaysActive') }}</div>
            </div>
            <input type="checkbox" checked disabled class="custom-checkbox" />
          </div>
          <div class="text-sm text-gray-600 mt-2">
            <span v-html="$t('cookieConsent.services.matomo.description')"></span>
          </div>
        </div>

        <div
          v-for="service in serviceList"
          :key="service.key"
          class="flex flex-col border-b last:border-b-0 border-gray-200 py-3"
        >
          <div class="flex items-center justify-between">
            <div>
              <div class="font-bold">
                {{ $t(`cookieConsent.services.${service.key}.title`) }}
              </div>
              <span
                :class="[
                  'text-xs font-bold',
                  preferencesValue[service.key] ? 'text-green-600' : 'text-red-600'
                ]"
              >
                {{ preferencesValue[service.key]
                  ? $t('cookieConsent.statusActive')
                  : $t('cookieConsent.statusInactive') }}
              </span>
            </div>
            <input
              type="checkbox"
              :checked="preferencesValue[service.key]"
              @change="e => handleCheckboxChange(e, service.key)"
              class="custom-checkbox"
            />
          </div>
          <div class="text-sm text-gray-600 mt-2">
            <span v-html="$t(`cookieConsent.services.${service.key}.description`)"></span>
          </div>
        </div>
      </div>

      <div class="flex flex-col sm:flex-row gap-3 pt-4 border-t border-gray-200">
        <button @click="handleAcceptSelected" class="btn btn-secondary">
          {{ $t('cookieConsent.confirmSelection') }}
        </button>
        <button @click="handleAcceptAll" class="btn btn-primary">
          {{ $t('cookieConsent.selectAll') }}
        </button>
      </div>
    </div>
  </SharedModal>
</template>
